<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import KpButton from '@/components/KpButton.vue';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { KennzahlNutzungsart, KennzahlNutzungsartCollection } from '@/models/KennzahlNutzungsart';
import { NutzungsartCollection, type Nutzungsart } from '@/models/Nutzungsart';
import KpSelectField from '@/components/KpSelectField.vue';
import CopyNutzungsartDialog from '@/components/CopyNutzungsartDialog.vue';
import { instance as api } from '@/helpers/api';
import type { DataTableRowExpandEvent } from 'primevue/datatable';

const router = useRouter();
const route = useRoute();
const toast = useToast();
const confirm = useConfirm();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'nutzungsart_reihenfolge' },
    { direction: 'asc', property: 'bauperiode_code' },
]);
const expandedRowGroups: Ref<string[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-expandedRowGroups`, []);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, -1);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new KennzahlNutzungsartCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

const nutzungsarten: Ref<Nutzungsart[]> = ref([]);
onMounted(async () => {
    if (collection.loading.value !== true) {
        collection.query();
    }
    const col = new NutzungsartCollection();
    col.setQueryParams({
        sort: [
            {
                property: 'reihenfolge',
                direction: 'asc',
            },
        ],
    });
    await col.query();
    col.each((m) => nutzungsarten.value.push(m));
});

function editRecord(r: KennzahlNutzungsart) {
    router.push({ name: 'bg_geb_kennzahl_nutzungsart_edit', params: { id: r?.id } });
}

async function deleteRecord(r: KennzahlNutzungsart) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function onRowExpanded(event: DataTableRowExpandEvent) {
    expandedRowGroups.value = [event.data];
}

function newRecord() {
    router.push({ name: 'bg_geb_kennzahl_nutzungsart_edit', params: { id: 'new' } });
}

const copyFromId = ref(0);
const showCopyDialog = ref(false);
function openCopyDialog(nutzungsartId: number) {
    copyFromId.value = nutzungsartId;
    showCopyDialog.value = true;
}

async function executeCopy(id: number) {
    if (copyFromId.value === id) {
        toast.add({
            severity: 'error',
            summary: getI18n().t('fehler'),
            detail: 'Die Nutzungsarten sind identisch!',
        });
        return;
    }

    // Pruefen, ob die Ziel-Nutzungsart bereits Kennzahlen besitzt. Falls ja, vor dem
    // Ueberschreiben warnen (das Backend ersetzt die bestehenden Eintraege).
    const existing = new KennzahlNutzungsartCollection();
    existing.setQueryParams({ filter: [{ property: 'nutzungsart_id', value: id }] });
    await existing.query();
    let targetHasEntries = false;
    existing.each(() => {
        targetHasEntries = true;
    });

    if (targetHasEntries) {
        confirm.require({
            header: getI18n().t('achtung'),
            message:
                'ACHTUNG: Die Kennzahlen Nutzungsarten sind für diesen Eintrag bereits vorhanden. ' +
                'Wenn Sie weiterfahren, werden die bestehenden Einträge überschrieben.',
            rejectProps: {
                label: getI18n().t('nein'),
                severity: 'secondary',
                outlined: true,
            },
            acceptProps: {
                label: getI18n().t('ja'),
            },
            accept: () => copyTo(id),
        });
        return;
    }

    copyTo(id);
}

async function copyTo(id: number) {
    try {
        await api().post('/KennzahlNutzungsart/copy', {
            from_nutzungsart_id: copyFromId.value,
            to_nutzungsart_id: id,
        });
        reloadCollection();
    } catch (e) {
        errToToast(e, toast);
    }
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            v-model:expandedRowGroups="expandedRowGroups"
            :title="$t('kennzahlen_nutzungsarten')"
            scrollable
            scroll-height="flex"
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            expandableRowGroups
            rowGroupMode="subheader"
            groupRowsBy="nutzungsart"
            @rowgroup-expand="onRowExpanded"
            @editrecord="editRecord($event as KennzahlNutzungsart)"
            @deleterecord="deleteRecord($event as KennzahlNutzungsart)"
            @new="newRecord"
        >
            <template #groupheader="slotProps">
                <div class="inline-flex justify-content-between flex-wrap">
                    <div class="flex align-items-center justify-content-left ml-2 font-bold leading-normal">
                        {{ slotProps.data.nutzungsart }}
                    </div>
                    <div class="flex align-items-center justify-content-right">
                        <KpButton
                            v-tooltip.bottom="$t('kopieren')"
                            icon="far fa-copy"
                            size="small"
                            severity="contrast"
                            variant="text"
                            rounded
                            @click="openCopyDialog(slotProps.data.nutzungsart_id)"
                        />
                    </div>
                </div>
            </template>
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="nutzungsart" :header="$t('nutzungsart')" filter-field="nutzungsart_id">
                <template #body="slotProps">
                    <div>{{ slotProps.data.nutzungsart }}</div>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <KpSelectField
                        fluid
                        v-model="filterModel.value"
                        :options="nutzungsarten"
                        option-label="bezeichnung"
                        option-value="id"
                        @change="filterCallback()"
                        :showClear="true"
                    ></KpSelectField>
                </template>
            </Column>
            <Column field="bauperiode_i18n_key" :header="$t('bauperiode')">
                <template #body="slotProps">
                    <div>{{ $t(slotProps.data.bauperiode_i18n_key) }}</div>
                </template>
            </Column>
            <!-- Zur besseren Übersicht werden diese Spalten ausgeblendet -->
            <!-- <Column
                field="umrechnungsfaktor_geschossflaeche_ebf"
                :header="$t('umrechnungsfaktor_geschossflaeche_ebf')"
                style="max-width: 5rem"
            ></Column>
            <Column field="stuetzpunkt_unten" :header="$t('stuetzpunkt_unten')" style="max-width: 5rem"></Column>
            <Column
                field="standadverbauch_stuetzpunkt_unten"
                :header="$t('standadverbauch_stuetzpunkt_unten')"
                style="max-width: 5rem"
            ></Column>
            <Column field="stuetzpunkt_oben" :header="$t('stuetzpunkt_oben')" style="max-width: 5rem"></Column>
            <Column
                field="standadverbauch_stuetzpunkt_oben"
                :header="$t('standadverbauch_stuetzpunkt_oben')"
                style="max-width: 5rem"
            ></Column>
            <Column
                field="abweichung_standardverbrauch"
                :header="$t('abweichung_standardverbrauch')"
                style="max-width: 5rem"
            ></Column>
            <Column
                field="warmwasserbedarf_pro_wohneinheit"
                :header="$t('warmwasserbedarf_pro_wohneinheit')"
                style="max-width: 5rem"
            ></Column>
            <Column
                field="warmwasserbedarf_pro_m2_ebf"
                :header="$t('warmwasserbedarf_pro_m2_ebf')"
                style="max-width: 5rem"
            ></Column>
            <Column
                field="begrenzung_energiebezugsflaeche"
                :header="$t('begrenzung_energiebezugsflaeche')"
                style="max-width: 5rem"
            ></Column> -->
        </KpBaseCollectionGrid>
        <CopyNutzungsartDialog
            v-if="showCopyDialog"
            v-model="showCopyDialog"
            @copy="executeCopy"
        ></CopyNutzungsartDialog>
        <ConfirmDialog></ConfirmDialog>
    </div>
</template>
